import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    Dialog,
    DialogContent,
    DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Check, Loader2, ArrowRight } from "lucide-react";
import IndividualSubscriptionService from "../../services/subscription/individualSubscription";
import { toastError, toastSuccess } from "../ToastNotification";

// Import a generic User icon (Lucide's User icon)
import { User as UserIcon } from "lucide-react";

let globalIndFetched = false;
let globalIndData = null;
let globalPlanId = null;
let globalIndUserKey = null;

export default function IndividualPopup({
    triggerLabel,
    open: controlledOpen,
    onOpenChange,
    hideTrigger = false,
}) {
    const [loading, setLoading] = useState(false);
    const [subscriptionData, setSubscriptionData] = useState(globalIndData);
    const [planId, setPlanId] = useState(globalPlanId);
    const [internalOpen, setInternalOpen] = useState(false);
    const user = useSelector((state) => state?.superAdmin?.user);
    const currentUserKey = user?._id || user?.id || user?.email || null;

    const isControlled = controlledOpen !== undefined;
    const dialogOpen = isControlled ? controlledOpen : internalOpen;

    const handleDialogOpenChange = (openState) => {
        if (!isControlled) setInternalOpen(openState);
        onOpenChange?.(openState);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const fetchData = async (force = false) => {
        if (globalIndFetched && !force) {
            setSubscriptionData(globalIndData);
            setPlanId(globalPlanId);
            return;
        }
        try {
            // First fetch the plans to get the plan_id
            const plansRes = await IndividualSubscriptionService.getSubscription();
            if (plansRes?.data?.data && Array.isArray(plansRes.data.data)) {
                // If the array has items, use the first one's id
                if (plansRes.data.data.length > 0) {
                    globalPlanId = plansRes.data.data[0].id || plansRes.data.data[0]._id;
                    setPlanId(globalPlanId);
                }
            } else if (plansRes?.data?.data) {
                globalPlanId = plansRes.data.data.id || plansRes.data.data._id;
                setPlanId(globalPlanId);
            }

            // Then fetch the active subscription
            const activeRes = await IndividualSubscriptionService.activeSubscription();
            console.log("🚀  activeRes:", activeRes);
            if (activeRes?.data?.success) {
                // Determine structure based on how API typically returns
                globalIndData = activeRes?.data?.data?.active_subscription;
                setSubscriptionData(globalIndData);
                globalIndFetched = true;
            }
        } catch (error) {
            console.log("🚀  fetchData  error:", error);
        }
    };

    const handleCreateSubscription = async () => {
        try {
            setLoading(true);
            const response = await IndividualSubscriptionService.createCheckoutSession();

            console.log("🚀  handleCreateSubscription  response:", response?.data);

            if (response?.data?.success && (response?.data?.data?.checkout_url || response?.data?.data?.url)) {
                window.location.href = response.data.data.checkout_url || response.data.data.url;
            }
        } catch (error) {
            console.log("🚀  handleCreateSubscription  error:", error);
            toastError(error?.response?.data?.message || "Failed to create subscription");
        } finally {
            setLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (window.confirm("Are you sure you want to cancel your subscription? This action cannot be undone.")) {
            try {
                setLoading(true);
                // For cancel, it expects `id` which the user named plan_id in the parameter
                const cancelId = subscriptionData?._id || subscriptionData?.id || planId;
                const response = await IndividualSubscriptionService.cancelSubscription(cancelId);
                if (response?.data?.success) {
                    toastSuccess(response?.data?.message || "Subscription cancelled successfully");
                    fetchData(true);
                }
            } catch (error) {
                console.log("🚀  handleCancelSubscription  error:", error);
                toastError(error?.response?.data?.message || "Failed to cancel subscription");
            } finally {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!currentUserKey) {
            setSubscriptionData(null);
            setPlanId(null);
            return;
        }

        const userChanged = globalIndUserKey !== currentUserKey;
        if (userChanged) {
            globalIndFetched = false;
            globalIndData = null;
            globalPlanId = null;
            setSubscriptionData(null);
            setPlanId(null);
        }

        fetchData(userChanged);
        globalIndUserKey = currentUserKey;
    }, [currentUserKey]);

    const features = [
        "Monthly subscription access",
        "Upload unlimited documents",
        "AI-powered document analysis",
        "Get answers based on document content",
        "24/7 platform access",
        "Email support",
    ];

    const buttonLabel =
        triggerLabel || (subscriptionData ? "Manage Subscription" : "Subscription");

    return (
        <div>
            <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
                {!hideTrigger && (
                    <DialogTrigger asChild>
                        <Button
                            variant="outline"
                            className="flex gap-2 bg-transparent border-primary text-primary hover:bg-primary hover:text-white"
                        >
                            {buttonLabel}
                        </Button>
                    </DialogTrigger>
                )}
                <DialogContent className="sm:max-w-[480px] bg-[#13131A] text-white border-0 p-10 shadow-2xl rounded-2xl">
                    <div className="flex flex-col items-center">
                        {/* Circular Icon */}
                        <div className="w-16 h-16 bg-[#1A1A24] rounded-full flex items-center justify-center mb-6">
                            <UserIcon className="w-8 h-8 text-[#3980FB]" strokeWidth={2} />
                        </div>

                        {/* Title */}
                        <h2 className="text-3xl font-bold text-center text-white mb-6">
                            Individual Subscription
                        </h2>

                        {/* Price */}
                        <div className="flex items-baseline justify-center mb-8">
                            <span className="text-[44px] leading-none font-bold text-white">$300</span>
                            <span className="text-[#8C92A6] ml-2 text-base font-medium">per month</span>
                        </div>

                        {/* Feature List */}
                        <div className="w-full space-y-4 mb-10 px-2">
                            {features.map((feature, index) => (
                                <div key={index} className="flex items-center gap-4 text-[#8C92A6] text-sm font-medium">
                                    <Check className="w-[18px] h-[18px] text-[#3980FB] shrink-0" strokeWidth={3} />
                                    {feature}
                                </div>
                            ))}
                        </div>

                        {/* Subscription State */}
                        {!subscriptionData ? (
                            <Button
                                onClick={handleCreateSubscription}
                                className="w-full bg-[#276EF1] hover:bg-[#1C5CDA] text-white py-6 h-[56px] text-lg font-semibold rounded-xl flex items-center justify-center gap-2"
                                disabled={loading || !planId}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                        Please wait
                                    </>
                                ) : (
                                    <>
                                        Subscribe Now <ArrowRight strokeWidth={2.5} className="w-5 h-5 ml-1" />
                                    </>
                                )}
                            </Button>
                        ) : (
                            <div className="w-full bg-primary/10 border border-primary/20 rounded-xl p-5 mt-2">
                                <div className="flex items-center gap-2 text-primary mb-3">
                                    <Check strokeWidth={2.5} className="w-5 h-5 bg-primary/20 rounded-full p-1" />
                                    <span className="font-semibold text-lg">Active Subscription</span>
                                </div>
                                <div className="space-y-1.5 text-sm text-[#8C92A6]">
                                    {/* <div className="flex justify-between">
                                        <span>Seats:</span>
                                        <span className="text-white font-medium">{subscriptionData.purchased_seats || 1}</span>
                                    </div> */}
                                    <div className="flex justify-between">
                                        <span>{subscriptionData.cancel_at_period_end ? " Expiry Date: " : " Renewal Date: "}</span>
                                        <span className="text-white font-medium">{formatDate(subscriptionData.current_period_end)}</span>
                                    </div>
                                </div>

                                {!subscriptionData.cancel_at_period_end && (
                                    <Button
                                        variant="destructive"
                                        onClick={handleCancelSubscription}
                                        className="w-full mt-5 bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500 hover:text-white transition-all duration-300"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Cancelling...
                                            </>
                                        ) : (
                                            "Cancel Subscription"
                                        )}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}